import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Card, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { HRApiWrapper } from 'api/HRApiWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from '../Spinner';
import SearchInput from 'components/SearchInput';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { useDialog } from 'common/DialogProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { ScopeDTO } from 'axiosApi/models';

const INITIAL_STATE: GridInitialState = {
  pagination: { paginationModel: { page: 0, pageSize: 20 } },
};

const ScopeList = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const [rows, setRows] = useState<ScopeDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'scope', sort: 'asc' }]);

  const fetchScopes = useCallback(async () => {
    setLoading(true);
    const hrApi = new HRApiWrapper(api.employeeApi);
    const sortField = sortModel[0]?.field === 'id' ? 'id' : 'description';
    const sortOrder = sortModel[0]?.sort ?? 'asc';
    try {
      const res = await hrApi.getScopesAll('1', page + 1, pageSize, searchTerm || undefined, sortField, sortOrder);
      const data = res.data?.data;
      setRows(data?.queryResult ?? []);
      setTotalRecords(data?.totalRecords ?? 0);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, sortModel]);

  useEffect(() => { fetchScopes(); }, [fetchScopes]);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await api.employeeApi.apiVversionScopeIdDelete(id, '1', {});
            toast.success({ message: 'Scope deleted' });
            fetchScopes();
          } catch (e) {
            handleAPIError(e, toast, {});
          } finally {
            closeDialog(dialogId);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'scope', headerName: 'Description', flex: 1 },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={ENTITY_TYPE.ANY}
          onEdit={() => navigate(`/scope/detail/${params.row.id}`)}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={[]}
          deleteScopes={[]}
        />
      )
    }
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" sx={{ mb: 2 }}>Scopes</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SearchInput
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); setPage(0); }}
            onClear={() => { setSearchTerm(''); setPage(0); }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/scope/new')}>
            New Scope
          </Button>
        </Box>
        <Box style={{ width: '100%', display: 'flex' }}>
          {loading && <Spinner />}
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={totalRecords}
            loading={loading}
            initialState={INITIAL_STATE}
            paginationMode="server"
            sortingMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            sortModel={sortModel}
            onSortModelChange={(m) => { setSortModel(m); setPage(0); }}
            pageSizeOptions={[5, 10, 20, 50]}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
    </Card>
  );
};

export default ScopeList;
